import { useEffect, useState } from "react";

import type { NoteReply, PatientNote } from "../domain/types";
import { noteToDateKey, toDateKey } from "../domain/patientNotes";
import { PortalAPI } from "../infrastructure/portalApi";
import { toLocalDateKey } from "../../../shared/utils/date";

export type NoteSelection = { type: "new" } | { type: "existing"; note: PatientNote };

/**
 * The patient's own notes in the portal: the paged list, every note for the
 * calendar, the note being edited, and the therapist's replies to a shared one.
 */
export function usePatientNotes() {
  // List view state
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Shared editor state
  const [selection, setSelection] = useState<NoteSelection | null>(null);
  const [editorBody, setEditorBody] = useState("");
  const [editorTitle, setEditorTitle] = useState("");
  const [shared, setShared] = useState(false);
  const [noteDate, setNoteDate] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mobileView, setMobileView] = useState<"list" | "editor">("list");

  // Calendar state
  const [viewMode, setViewMode] = useState<"list" | "calendar">("calendar");
  const [allNotes, setAllNotes] = useState<PatientNote[]>([]);
  const [allNotesLoading, setAllNotesLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Replies state
  const [replies, setReplies] = useState<NoteReply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadPage = (p: number) => {
    setLoading(true);
    PortalAPI.getNotes(p)
      .then(({ data }) => {
        setNotes(data.items);
        setTotalPages(data.pages);
        setPage(data.page);
      })
      .finally(() => setLoading(false));
  };

  const fetchAllNotes = async () => {
    setAllNotesLoading(true);
    try {
      const first = await PortalAPI.getNotes(1);
      const { items, pages } = first.data;
      const all = [...items];
      for (let p = 2; p <= pages; p++) {
        const { data } = await PortalAPI.getNotes(p);
        all.push(...data.items);
      }
      setAllNotes(all);
    } finally {
      setAllNotesLoading(false);
    }
  };

  useEffect(() => { loadPage(1); fetchAllNotes(); }, []);

  const selectedNoteId = selection?.type === "existing" ? selection.note.id : null;
  const selectedNoteShared = selection?.type === "existing" ? selection.note.shared_with_therapist : false;

  useEffect(() => {
    if (!selectedNoteId || !selectedNoteShared) { setReplies([]); return; }
    setRepliesLoading(true);
    PortalAPI.getNoteReplies(selectedNoteId)
      .then(({ data }) => setReplies(data))
      .catch(() => setReplies([]))
      .finally(() => setRepliesLoading(false));
  }, [selectedNoteId, selectedNoteShared]);

  const handleSubmitReply = async (body: string) => {
    if (!selectedNoteId) return;
    setReplySubmitting(true);
    try {
      const { data } = await PortalAPI.createNoteReply(selectedNoteId, body);
      setReplies((prev) => [...prev, data]);
    } finally {
      setReplySubmitting(false);
    }
  };

  // ── View switching ──────────────────────────────────────────────────────────

  const switchView = (mode: "list" | "calendar") => {
    setViewMode(mode);
    setSelection(null);
    setMobileView("list");
    if (mode === "calendar" && allNotes.length === 0 && !allNotesLoading) fetchAllNotes();
  };

  // ── Selection ───────────────────────────────────────────────────────────────

  const selectNote = (note: PatientNote) => {
    setSelection({ type: "existing", note });
    setEditorBody(note.body);
    setEditorTitle(note.title ?? "");
    setShared(note.shared_with_therapist);
    setNoteDate(note.note_date ?? toDateKey(note.created_at));
    setShowDeleteConfirm(false);
    setMobileView("editor");
  };

  const selectNew = (prefillDate?: string) => {
    setSelection({ type: "new" });
    setEditorBody("");
    setEditorTitle("");
    setShared(false);
    setNoteDate(prefillDate ?? toLocalDateKey());
    setShowDeleteConfirm(false);
    setMobileView("editor");
  };

  // ── Save / delete ───────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selection) return;
    const safeBody = typeof editorBody === "string" ? editorBody : "";
    const safeTitle = typeof editorTitle === "string" && editorTitle.trim() ? editorTitle.trim() : undefined;
    const safeShared = shared === true;
    const safeDate = typeof noteDate === "string" && noteDate ? noteDate : undefined;
    setSaving(true);
    try {
      if (selection?.type === "new") {
        const { data } = await PortalAPI.createNote(safeBody, safeTitle, safeShared, undefined, safeDate);
        setNotes((prev) => [data, ...prev]);
        setAllNotes((prev) => [data, ...prev]);
        setSelection({ type: "existing", note: data });
        if (viewMode === "calendar") {
          setSelectedDay(noteToDateKey(data));
        }
      } else if (selection?.type === "existing") {
        const { data } = await PortalAPI.updateNote(
          selection.note.id,
          safeBody,
          safeTitle,
          safeShared,
          safeDate,
        );
        setNotes((prev) => prev.map((n) => (n.id === data.id ? data : n)));
        setAllNotes((prev) => prev.map((n) => (n.id === data.id ? data : n)));
        setSelection({ type: "existing", note: data });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (selection?.type !== "existing") return;
    const deletedId = selection.note.id;
    setDeleting(true);
    try {
      await PortalAPI.deleteNote(deletedId);
      const remaining = notes.filter((n) => n.id !== deletedId);
      setAllNotes((prev) => prev.filter((n) => n.id !== deletedId));
      if (viewMode === "list") {
        if (remaining.length === 0 && page > 1) loadPage(page - 1);
        else loadPage(page);
        setMobileView("list");
      }
      setSelection(null);
      setEditorBody("");
      setEditorTitle("");
      setShared(false);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  return {
    notes,
    page,
    totalPages,
    loading,
    selection,
    setSelection,
    editorBody,
    setEditorBody,
    editorTitle,
    setEditorTitle,
    shared,
    setShared,
    noteDate,
    setNoteDate,
    saving,
    deleting,
    showDeleteConfirm,
    setShowDeleteConfirm,
    mobileView,
    setMobileView,
    viewMode,
    allNotes,
    allNotesLoading,
    selectedDay,
    setSelectedDay,
    currentMonth,
    setCurrentMonth,
    replies,
    repliesLoading,
    replySubmitting,
    loadPage,
    handleSubmitReply,
    switchView,
    selectNote,
    selectNew,
    handleSave,
    handleDelete,
  };
}
